import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { INPUT_TYPES, ValidationErrorCode, PasswordSource, KeyBoardAction } from '../../commonUtil/constants';
import UxEventService from '../../service/UxEventService';
import { UXContextType, browserMatch, isMobileDevice } from '../../commonUtil/util';
import { useLoadManifest } from '../../commonUtil/hookUtil';
import { withLinkStore, ACTION_TYPES_LOADING_MANIFEST_DATA, ACTION_TYPES_IS_SKIP_SPLASH_SCREEN } from '../../app/reducers';
import { isGDPROn } from '../../commonUtil/gdprUtil';

const PasswordValidator = (props) => {
    const { t } = useTranslation();
    const [password, setPassword] = useState('');
    const [submitPasswordValue, setSubmitPasswordValue] = useState({});
    const [passwordType, setPasswordType] = useState(INPUT_TYPES.PASSWORD);
    const [isInputFocus, setOnFocusInput] = useState(false);
    const [isError, setError] = useState(false);
    const [isEnabledSubmitSpinner, setEnabledSubmitSpinner] = useState(true);

    const { linkDispatch } = props;
    const { gdprSetting, viewerLocation, manifests, viewerSetting, errorCode, isLoadingManifest } = props.linkStore;
    const request = PasswordSource.LINK_PASSWORD === props.source ? { password, compliancePassword: '' } : { password: '', compliancePassword: password };
    useLoadManifest(request.password, request.compliancePassword, submitPasswordValue);

    useEffect(() => {
        if (!isGDPROn(gdprSetting, viewerLocation) && manifests.length) {
            linkDispatch({ type: ACTION_TYPES_IS_SKIP_SPLASH_SCREEN, payload: true });
        }
    }, [submitPasswordValue, manifests]);

    const passwordInputRef = useRef();

    const handleOnFocus = () => setOnFocusInput(true);
    const handleOnBlur = () => setOnFocusInput(false);

    const onPasswordChange = () => {
        setError(false);
        const val = passwordInputRef.current.value.trim();
        setPassword(val);

        if (!val) {
            setPasswordType(INPUT_TYPES.PASSWORD);
        }
    };

    const onPasswordTypeChange = () => {
        if (passwordType === INPUT_TYPES.PASSWORD) {
            setPasswordType(INPUT_TYPES.TEXT);
        } else {
            setPasswordType(INPUT_TYPES.PASSWORD);
        }

        passwordInputRef.current.focus();
    };

    const handleValidatePassword = () => {
        const passwordValue = passwordInputRef.current.value.trim();
        setSubmitPasswordValue({ passwordValue });
    };

    const handleKeyDown = (e) => {
        // the keyCode of "Enter" is 13
        if (e.keyCode === KeyBoardAction.enter) {
            handleValidatePassword();
        }
    };

    const validatePassword = () => {
        const val = passwordInputRef.current && passwordInputRef.current.value.trim();
        if (!val) {
            props.linkDispatch({ type: ACTION_TYPES_LOADING_MANIFEST_DATA, payload: { isLoading: false } });
            return;
        }

        const isPasswordCorrect = errorCode === ValidationErrorCode.LIVESEND_MANIFEST_OK;

        if (isPasswordCorrect) {
            setError(false);
            UxEventService.navigateViewEvent({ Context: UXContextType.PASSWORD, Target: 'Correct Password' });
            props.setPasswordValidation(true);
        } else if (!isLoadingManifest) {
            setError(true);
            passwordInputRef.current.focus();
            UxEventService.navigateViewEvent({ Context: UXContextType.PASSWORD, Target: 'Failed Password' });
            props.setPasswordValidation(false);
        }
    };

    useEffect(() => {
        validatePassword();
    }, [isLoadingManifest]);

    const inputFieldClassname = classnames('input-field', { 'input-error': isError && !isLoadingManifest, 'input-active': isInputFocus });
    const btnClassname = classnames('enagement-buyer-btn seismic-engagement-password-validator-btn', { 'btn-active': password && !isLoadingManifest });
    const inputClassname = classnames('validator-input flex-auto', {
        'validator-input-password-asterisk': passwordType === INPUT_TYPES.PASSWORD && password,
        'small-font-asterisk': passwordType === INPUT_TYPES.PASSWORD && password && (browserMatch.Edge() || browserMatch.IE()),
        'ios-font-asterisk': passwordType === INPUT_TYPES.PASSWORD && password && (isMobileDevice.iOS() || isMobileDevice.MAC())
    });

    const getButtonStyle = () => {
        if (password && isLoadingManifest && isEnabledSubmitSpinner) {
            return { backgroundColor: viewerSetting.brandingHilightColor, cursor: 'wait' };
        } else if (password && !isLoadingManifest) {
            return { backgroundColor: viewerSetting.brandingHilightColor };
        }

        return { cursor: 'not-allowed' };
    };

    const getPreviewLabel = () => {
        if (passwordType === INPUT_TYPES.TEXT) {
            return t('LIVE_SEND_VIEWER_HIDE');
        } else if (password) {
            return t('LIVE_SEND_VIEWER_SHOW');
        }

        return null;
    };

    const passwordSubmitDirectAccess = () => {
        setEnabledSubmitSpinner(true);
        handleValidatePassword();
    };

    const onKeydownSubmitDirectAccess = (e) => {
        if (e.keyCode === KeyBoardAction.enter) {
            passwordSubmitDirectAccess();
        }
    };

    const onKeyDownShowPassword = (e) => {
        if (e.keyCode === KeyBoardAction.enter) {
            onPasswordTypeChange();
        }
    };

    return (
        [
            <div key="PasswordInputWrapper" className="seismic-engagement-password-validator">
                <div className={inputFieldClassname} onFocus={handleOnFocus} onBlur={handleOnBlur}>
                    {(isInputFocus || password) && <label htmlFor="passwordInput" className="validator-label">{t('LIVE_SEND_VIEWER_PASSWORD')}</label>}
                    <div className={`d-flex justify-content-between align-items-center input-line ${isLoadingManifest ? 'disabled' : ''}`}>
                        <input
                            id="passwordInput"
                            className={inputClassname}
                            disabled={isLoadingManifest}
                            ref={passwordInputRef}
                            type={passwordType}
                            value={password}
                            aria-label={props.placeholder || t('LIVE_SEND_VIEWER_PASSWORD_PLACEHOLDER')}
                            placeholder={props.placeholder || t('LIVE_SEND_VIEWER_PASSWORD_PLACEHOLDER')}
                            onChange={onPasswordChange}
                            onKeyDown={handleKeyDown} />
                        <div className="flex-none pwd-type-label" role="button" tabIndex="0" onClick={onPasswordTypeChange} onKeyDown={onKeyDownShowPassword}>
                            {getPreviewLabel()}
                        </div>
                    </div>
                </div>
                {isError && !isLoadingManifest && <div className="seismic-engagement-password-validator-error-msg"> {t('LIVE_SEND_VIEWER_PASSWORD_ERROR_MSG')} </div>}
            </div>,
            <button key="PasswordSubmitButton"
                style={getButtonStyle()}
                className={btnClassname}
                disabled={isLoadingManifest}
                onKeyDown={onKeydownSubmitDirectAccess}
                onClick={passwordSubmitDirectAccess}>
                {isLoadingManifest && isEnabledSubmitSpinner && <div className="engagement-viewer-busy-spinner" />}
                {isLoadingManifest && isEnabledSubmitSpinner ? '' : t('LIVE_SEND_VIEWER_SUBMIT')}
            </button>
        ]
    );
};

export default withLinkStore(PasswordValidator);
